import React, { useCallback } from 'react';
import { Checkbox, Typography } from '@mui/material';
import styled from 'styled-components';

import { Action } from 'state';
import Choice from 'types/Choice';
import Field, { getNestedFieldsByChoice } from 'types/Field';
import { colors } from 'theme';
import Section from 'types/Section';
import NestedSelectFieldPreview from './NestedSelectFieldPreview';

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    padding: 0;
  }

  &.MuiCheckbox-colorSecondary.Mui-checked {
    color: ${colors.primary};
  }
`;

const StyledChoiceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  margin: 0;
  margin-bottom: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  height: 2rem;

  background-color: ${colors.blueGrey_10};
`;

interface MultiSelectFieldPreviewProps {
  field: Field;
  section: Section;
  dispatch: (action: Action) => void;
}

const MultiSelectFieldPreview: React.FC<MultiSelectFieldPreviewProps> = ({ field, section, dispatch }) => {
  const nestedFieldsByChoice = getNestedFieldsByChoice(field, section.exam_fields);

  const handleOnChange = useCallback(
    (choice: Choice) => {
      dispatch({ type: 'toggle-multi-choice-preview', section, field, choice });
    },
    [dispatch, section, field],
  );

  const renderNestedFieldRow = (choice: Choice): React.ReactNode => {
    const nestedField = nestedFieldsByChoice[choice.uuid];

    if (!nestedField) return null;
    if (!field.preview_default_value.includes(choice.uuid)) return null;

    return <NestedSelectFieldPreview section={section} field={nestedField} dispatch={dispatch} />;
  };

  return (
    <>
      <Typography variant="subtitle2">{field.label}</Typography>
      {field.choices_json.map((choice) => (
        <div key={choice.uuid}>
          <StyledChoiceRow key={choice.uuid}>
            <Typography variant="body2">{choice.label}</Typography>
            <StyledCheckbox
              checked={field.preview_default_value.includes(choice.uuid)}
              value={choice.uuid}
              onChange={() => handleOnChange(choice)}
            />
          </StyledChoiceRow>
          {renderNestedFieldRow(choice)}
        </div>
      ))}
    </>
  );
};

export default MultiSelectFieldPreview;
